import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import debounce from 'lodash.debounce';
import * as R from 'ramda';

import ComboSelect from '@atom/components/common/comboSelect/ComboSelect';
import { GET_BUDGET_OPTIONS, SEARCH_BUDGET_OPTIONS } from '@atom/graph/budget';
import { Snackbar } from '@atom/mui';
import { BasicBudget } from '@atom/types/budget';
import { objectIdsMatch } from '@atom/utilities/objectCompareUtilities';

import BudgetDetailContext from '../BudgetDetailContext';
import {
  DEBOUNCE_TIME,
  getDateRange,
  getMonthRange,
  getStatusLabel,
} from '../budgetDetailUtils';
import { FILTER_KEYS, useBudgetFilterStorage } from '../useBudgetFilterStorage';

const LIMIT = 25;
const MIN_SEARCH_CHARS = 2;
const MAX_SELECTIONS = 5;

const styles = {
  statusLabel: {
    paddingRight: '0.5rem',
  },
};

const BudgetDetailComparisonFilter = () => {
  const {
    comparisonBudgets,
    setComparisonBudgets,
    setBudgetSummaries,
    budget,
    showApprovalFlow,
  } = useContext(BudgetDetailContext);
  const { existingComparisonBudgets, saveFilterState } = useBudgetFilterStorage(
    budget.id,
  );

  const [comparisonBudgetsCart, setComparisonBudgetsCart] =
    useState<BasicBudget[]>(comparisonBudgets);
  const [comparisonBudgetsSnapshot, setComparisonBudgetsSnapshot] =
    useState<BasicBudget[]>(comparisonBudgets);

  const [query, setQuery] = useState<string>('');
  const [searchOptions, setSearchOptions] = useState<BasicBudget[]>([]);
  const [searchPage, setSearchPage] = useState<number>(1);
  const [searchTotal, setSearchTotal] = useState<number>(0);

  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [open, setOpen] = useState<boolean>();
  const [options, setOptions] = useState<BasicBudget[]>(
    existingComparisonBudgets,
  );

  const searching = useMemo(() => query.length >= MIN_SEARCH_CHARS, [query]);

  const [
    fetchSearchOptions,
    {
      loading: loadingSearchOptions,
      data: searchOptionsData,
      error: searchOptionsError,
    },
  ] = useLazyQuery(SEARCH_BUDGET_OPTIONS, { fetchPolicy: 'no-cache' });

  useEffect(() => {
    if (!searchOptionsData) {
      return;
    }
    const nextOptions: BasicBudget[] = R.pathOr(
      [],
      ['budgetSearch', 'budgets'],
      searchOptionsData,
    );
    const newTotal: number = R.pathOr(
      0,
      ['budgetSearch', 'totalCount'],
      searchOptionsData,
    );
    const currentMonthRange = getMonthRange(budget);
    setSearchOptions(prev =>
      [...prev, ...nextOptions].filter(
        option => getMonthRange(option) === currentMonthRange,
      ),
    );
    setSearchTotal(newTotal);
  }, [searchOptionsData]);

  useEffect(() => {
    if (searchOptionsError) {
      Snackbar.error({ message: 'Failed to search comparison options.' });
    }
  }, [searchOptionsError]);

  const [
    fetchBudgetOptions,
    { loading: loadingOptions, data: budgetOptionsData },
  ] = useLazyQuery(GET_BUDGET_OPTIONS, { fetchPolicy: 'no-cache' });

  useEffect(() => {
    if (!budgetOptionsData) {
      return;
    }
    const nextOptions: BasicBudget[] = R.pathOr(
      [],
      ['budgetOptions', 'budgets'],
      budgetOptionsData,
    );
    const newTotal: number = R.pathOr(
      0,
      ['budgetOptions', 'totalCount'],
      budgetOptionsData,
    );
    const currentMonthRange = getMonthRange(budget);
    setOptions(prev =>
      R.uniqBy(R.prop('id'), [...prev, ...nextOptions]).filter(
        option => getMonthRange(option) === currentMonthRange,
      ),
    );
    setTotal(newTotal);
  }, [budgetOptionsData]);

  const handleSelectOption = (option: BasicBudget) => {
    const selectedIds = comparisonBudgetsCart.map(comp => comp.id);
    const newSelectedOptions = selectedIds.includes(option.id)
      ? comparisonBudgetsCart.filter(comp => comp.id !== option.id)
      : [...comparisonBudgetsCart, option];
    setComparisonBudgetsCart(newSelectedOptions);
  };

  const handleClearSelections = () => {
    setComparisonBudgets([]);
    setComparisonBudgetsCart([]);
    saveFilterState(FILTER_KEYS.COMPARE, []);
  };

  const fetchSearchOptionsDebounced = useCallback(
    debounce((value: string) => {
      fetchSearchOptions({
        variables: {
          input: {
            budgetTemplateId: budget.templateId,
            query: value,
            limit: LIMIT,
            page: searchPage,
          },
        },
      });
    }, DEBOUNCE_TIME),
    [],
  );

  const handleQueryChange = (value: string = '') => {
    setQuery(value);
    setSearchPage(1);
    setSearchOptions([]);
    if (!value) {
      fetchSearchOptionsDebounced.cancel();
    } else if (value.length >= MIN_SEARCH_CHARS) {
      fetchSearchOptionsDebounced(value);
    } else {
      fetchSearchOptionsDebounced.cancel();
    }
  };

  const handlePageScroll = (nextPage: number) => {
    if (searching) {
      setSearchPage(nextPage);
      fetchSearchOptions({
        variables: {
          input: {
            budgetTemplateId: budget.templateId,
            query,
            limit: LIMIT,
            page: nextPage,
          },
        },
      });
    } else {
      setPage(nextPage);
      fetchBudgetOptions({
        variables: {
          input: {
            templateId: budget.templateId,
            limit: LIMIT,
            page: nextPage,
          },
        },
      });
    }
  };

  const removeSelf = (option: BasicBudget): boolean => option.id !== budget.id;
  const filteredOptions = useMemo(() => options.filter(removeSelf), [options]);
  const filteredSearchOptions = useMemo(
    () => searchOptions.filter(removeSelf),
    [searchOptions],
  );

  useEffect(() => {
    // Cache initial selected item snapshot when user opens menu
    if (open === true) {
      setComparisonBudgetsSnapshot(comparisonBudgetsCart);
    }
    if (
      open === false &&
      !objectIdsMatch(comparisonBudgetsSnapshot, comparisonBudgetsCart)
    ) {
      setComparisonBudgets(comparisonBudgetsCart);
      saveFilterState(FILTER_KEYS.COMPARE, comparisonBudgetsCart);
      setBudgetSummaries([]);
    }
  }, [open]);

  useEffect(() => {
    fetchBudgetOptions({
      variables: {
        input: {
          templateId: budget?.templateId,
          limit: LIMIT,
        },
      },
    });
  }, []);

  const getStatusIcon = showApprovalFlow
    ? option => (
        <span style={styles.statusLabel}>
          {getStatusLabel(option.status, true)}
        </span>
      )
    : null;

  return (
    <ComboSelect
      open={open}
      setOpen={setOpen}
      itemName="Budgets"
      loading={loadingOptions || loadingSearchOptions}
      options={searching ? filteredSearchOptions : filteredOptions}
      total={searching ? searchTotal : total}
      page={searching ? searchPage : page}
      handlePageScroll={handlePageScroll}
      heading={budget?.templateName}
      getOptionSubheading={getDateRange}
      query={query}
      onQueryChange={handleQueryChange}
      selectedOptions={comparisonBudgetsCart}
      clearSelections={() => handleClearSelections()}
      onSelectOption={handleSelectOption}
      dataCyLabel="BudgetComparisonFilter"
      maxSelections={MAX_SELECTIONS}
      getOptionIcon={getStatusIcon}
    />
  );
};

export default BudgetDetailComparisonFilter;
